import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Calendar,
  Users2,
  Pencil,
  CheckCircle2,
  MapPin,
  UserPlus,
  Globe,
  Building2,
  User,
} from "lucide-react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createEvent, updateEventWithInvites } from "@/lib/queries/events";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { getOpportunityByCatalogSlug } from "@/lib/queries/opportunities";
import { EventDetailsForm } from "@/components/events/create/EventDetailsForm";
import { getHostsAvailableOnDate } from "@/lib/queries/host";
import { supabase } from "@/integrations/supabase/client";
import { ISD_CODES } from "@/lib/constants";
import { useAuth } from "@/hooks/useAuth";
import { CANCELLATION_POLICIES } from "@/constants/forms";
import { LoadingDialog } from "@/components/ui/loading-dialog";
import SEO from "@/components/SEO";
import { formatDateWithTimezone } from "@/lib/constants/timezones";
import { Fragment } from "react";

// Language options
const languageOptions = [
  "English",
  "Spanish",
  "French",
  "German",
  "Chinese",
  "Japanese",
  "Hindi",
  "Arabic",
  "Portuguese",
  "Russian",
];

const checkoutFormSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  country_code: z.string().optional(),
  event_date: z
    .string()
    .min(1, "Event date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      // Set both dates to start of day for accurate comparison
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate difference in days
      const diffTime = selectedDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 14; // Minimum 2 weeks (14 days)
    }, "Event date must be at least 2 weeks in the future"),
  event_time: z.string().min(1, "Event time is required"),
  timezone: z.string().min(1, "Timezone is required"),
  location: z.string().min(1, "Location is required"),
  venue_address: z.string().optional(),
  preferred_language: z.string().min(1, "Preferred language is required"),
  team_name: z.string().optional(),
  volunteer_count: z.number().min(1, "At least 1 volunteer is required"),
  host_id: z.string().optional(),
});

type CheckoutFormData = z.infer<typeof checkoutFormSchema>;

interface PriceRecommendation {
  currency: string;
  cost: number;
  costUsd: number;
  pricingModel: string;
  additionalFees: Record<string, any>;
  includedFees: boolean;
  additionalDetails: string;
}

export default function AnonCheckout() {
  const navigate = useNavigate();
  const { npoSlug, catalogSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, profile, isLoading, signOut } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+65");
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [isMicrosite, setIsMicrosite] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      country_code: "",
      event_date: "",
      event_time: "",
      timezone: selectedTimezone,
      location: "",
      venue_address: "",
      preferred_language: "English",
      team_name: "",
      volunteer_count: 1,
      host_id: "",
    },
  });

  useEffect(() => {
    if (profile && profile.user_type === "company") {
      form.setValue("first_name", profile?.first_name);
      form.setValue("last_name", profile?.last_name);
      form.setValue("email", profile?.company_email);
      form.setValue("phone", profile?.phone);
      form.setValue("timezone", selectedTimezone);
      setSelectedCountryCode(profile?.isd_code);

      // Skip to section 2 if all required contact fields are filled
      if (profile?.first_name && profile?.last_name && profile?.company_email) {
        setCurrentStep(2);
      }
    }
    if (profile) {
      signOut();
    }
  }, [profile, signOut]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");

    if (sessionId) {
      setIsMicrosite(sessionStorage.getItem("microsite_exp") === "true");
      if (paymentStatus === "success") {
        toast({
          title: "Payment successful",
          description: "Your booking has been confirmed.",
        });

        if (isMicrosite) {
          // Post message to parent window and close
          window.opener?.postMessage(
            {
              type: "CHECKOUT_RESULT",
              success: true,
              message: "Payment successful",
            },
            "*"
          );
          window.close();
        } else {
          navigate("/checkout/thank-you");
        }
      } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
        toast({
          title: "Payment failed",
          description: "Please try again or contact support.",
          variant: "destructive",
        });

        if (isMicrosite) {
          // Post message to parent window and close
          window.opener?.postMessage(
            {
              type: "CHECKOUT_RESULT",
              success: false,
              message: "Payment failed",
            },
            "*"
          );
          window.close();
        } else {
          navigate("/checkout/payment-failed");
        }
      }
    }
  }, [navigate, toast]);

  useEffect(() => {
    const savedFormData = sessionStorage.getItem("savedFormData");

    if (savedFormData) {
      const parsedData = JSON.parse(savedFormData);
      Object.keys(parsedData).forEach((key) => {
        if (key === "countryCode") {
          setSelectedCountryCode(parsedData[key]);
        } else if (key === "timezone") {
          setSelectedTimezone(parsedData[key]);
        } else {
          form.setValue(key as any, parsedData[key]);
        }
      });
    }
  }, []);

  const { data: opportunity, error } = useQuery({
    queryKey: ["opportunity", catalogSlug],
    queryFn: async () => {
      const result = await getOpportunityByCatalogSlug(catalogSlug, npoSlug);
      return result;
    },
    enabled: !!catalogSlug,
    staleTime: 0, // Ensure we always get fresh data with price recommendations
  });

  const { data: availableHosts } = useQuery({
    queryKey: ["available-hosts", form.watch("event_date")],
    queryFn: () => getHostsAvailableOnDate(form.watch("event_date")),
    enabled: !!form.watch("event_date"),
  });

  useEffect(() => {
    if (currentStep === 1) {
      contactRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (currentStep === 2) {
      activityRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (currentStep === 3) {
      hostRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (currentStep === 4) {
      reviewRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentStep]);

  const eventDetails = {
    title: opportunity?.title || "N/A",
    description:
      opportunity?.opportunity?.description || "No description available",
    startDate: opportunity?.opportunity?.start_date
      ? new Date(opportunity.opportunity.start_date)
      : new Date(),
    duration: opportunity?.opportunity?.duration || "N/A",
    location: opportunity?.location || "Location not specified",
  };

  const getHostPrice = (host: any) => {
    if (!host) return "USD 0";
    const format = opportunity?.format;
    const hostRate = host.host_rate_card?.find(
      (card: any) => card.format === format
    );
    return `USD ${hostRate?.rate || 0}`;
  };

  // Function to get price from price recommendations
  const getPriceFromRecommendations = () => {
    if (!opportunity?.price_recommendations) return null;

    const opportunityId = opportunity?.opportunity_id;
    const priceData =
      opportunity.price_recommendations.opportunity_responses[opportunityId];

    if (!priceData) return null;

    return {
      currency: priceData.currency || "USD",
      cost: priceData.cost_per_unit || 0,
      costUsd: priceData.cost_per_unit_usd || 0,
      pricingModel: priceData.pricing_model || "per_event",
      additionalFees: priceData.additional_fees || {},
      includedFees: priceData.include_fees_in_base || false,
      additionalDetails: priceData.additional_details || "",
    };
  };

  const validateSection = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof CheckoutFormData)[] = [];
    let errorMessage = "";

    switch (step) {
      case 1:
        fieldsToValidate = ["first_name", "last_name", "email"];
        errorMessage = "Please fill in all required fields in the contact section";
        break;
      case 2:
        fieldsToValidate = [
          "event_date",
          "event_time", 
          "timezone",
          "location",
          "preferred_language",
          "volunteer_count"
        ];
        errorMessage = "Please complete all required fields in the activity section";
        break;
      case 3:
        if (!opportunity?.host_included) {
          fieldsToValidate = ["host_id"];
          errorMessage = "Please select a host";
        }
        break;
      default:
        return true;
    }

    if (fieldsToValidate.length === 0) {
      return true;
    }

    const result = await form.trigger(fieldsToValidate);
    
    if (!result) {
      // Get specific error messages from form state
      const errors = fieldsToValidate
        .map(field => {
          const error = form.formState.errors[field];
          return error ? `${field.replace('_', ' ')}: ${error.message}` : null;
        })
        .filter(Boolean);
      
      toast({
        title: "Validation Error",
        description: errors.length > 0 ? errors.join(", ") : errorMessage,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    const isValid = await validateSection(currentStep);
    if (!isValid) {
      return;
    }

    if (currentStep === 2 && opportunity?.host_included) {
      setCurrentStep(4);
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
    setEditingSection(null);
  };

  // Helper function to ensure time is in 24-hour format
  const ensureTime24HourFormat = (timeStr: string): string => {
    // If already in 24-hour format (no AM/PM), return as is
    if (!timeStr.includes("AM") && !timeStr.includes("PM")) {
      return timeStr;
    }

    // Extract hours, minutes, and period
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return timeStr; // Return original if format doesn't match

    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();

    // Convert to 24-hour format
    if (period === "PM" && hours < 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    // Format with leading zeros
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  const onSubmit = async (data: CheckoutFormData) => {
    // Validate all sections before submission
    const section1Valid = await validateSection(1);
    const section2Valid = await validateSection(2);
    const section3Valid = opportunity?.host_included || await validateSection(3);
    
    if (!section1Valid || !section2Valid || !section3Valid) {
      toast({
        title: "Validation Error",
        description: "Please complete all required fields before submitting",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setShowLoadingDialog(true);

    // Ensure timezone is set
    if (!data.timezone) {
      data.timezone = selectedTimezone;
    }

    // Always use the timezone from the form data
    const timezoneToUse = data.timezone;

    try {
      // Save form data and state to session storage
      const formDataToSave = {
        ...data,
        countryCode: selectedCountryCode,
        timezone: timezoneToUse,
      };
      sessionStorage.setItem("savedFormData", JSON.stringify(formDataToSave));

      // Call sign-in-otp edge function
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/hooks/otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: data.email,
            userData: {
              first_name: data.first_name,
              last_name: data.last_name,
              phone: data.phone,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send OTP");
      }

      const { data: signInData } = await response.json();

      const { userId } = signInData;
      sessionStorage.setItem("tempUserId", userId);

      if (userId) {
        // Update profiles using RPC
        const { error: txError } = await supabase.rpc(
          "update_corporate_profile",
          {
            user_id: userId,
            profile_full_name: `${data.first_name} ${data.last_name}`,
            corp_first_name: data.first_name,
            corp_last_name: data.last_name,
            corp_isd_code: selectedCountryCode,
            corp_phone: data.phone,
            corp_company_email: data.email,
          }
        );

        if (txError) {
          console.error("Error updating profiles:", txError);
          throw txError;
        }

        // Calculate event cost
        const priceRecommendation = getPriceFromRecommendations();
        let eventCost = 0;

        if (priceRecommendation) {
          // If price recommendation exists, calculate based on pricing model
          if (priceRecommendation.pricingModel === "per_volunteer") {
            // For per_volunteer pricing, multiply by the number of volunteers
            eventCost = priceRecommendation.costUsd * data.volunteer_count;
          } else {
            // For other pricing models (per_event, unlimited_package), use as is
            eventCost = priceRecommendation.costUsd;
          }

          // Handle additional fees based on their types
          if (
            !priceRecommendation.includedFees &&
            priceRecommendation.additionalFees
          ) {
            Object.entries(priceRecommendation.additionalFees).forEach(
              ([key, fee]: [string, any]) => {
                if (fee && fee.amount && fee.fee_type) {
                  if (fee.fee_type === "per_volunteer") {
                    // For per_volunteer fees, multiply by volunteer count
                    eventCost += (fee.amount_usd || 0) * data.volunteer_count;
                  } else if (fee.fee_type === "per_event") {
                    // For per_event fees, add directly
                    eventCost += fee.amount_usd || 0;
                  } else if (fee.fee_type === "total_program") {
                    // For total_program fees, add directly
                    eventCost += fee.amount_usd || 0;
                  }
                }
              }
            );
          }
        } else if (opportunity?.host_included) {
          // If host is included, only use opportunity cost
          eventCost = opportunity?.event_cost || 0;
        } else {
          // Calculate cost with host rate
          const selectedHost = data.host_id
            ? availableHosts?.find((host) => host.id === data.host_id)
            : null;
          const format = opportunity?.format;
          const hostRate = selectedHost?.host_rate_card?.find(
            (card: any) => card.format === format
          );
          eventCost = (hostRate?.rate || 0) + (opportunity?.event_cost || 0);
        }

        // Create or update the event with pending_payment status
        const {
          first_name,
          last_name,
          email,
          phone,
          country_code,
          host_id,
          volunteer_count,
          preferred_language,
          venue_address,
          team_name,
          ...eventData
        } = data;

        let createdEvent;
        const existingEventId = sessionStorage.getItem("checkOutItemId");
        if (existingEventId) {
          // Update existing event
          const { error: updateError } = await updateEventWithInvites({
            id: existingEventId,
            eventData: {
              ...eventData,
              opportunity_catalog_id: opportunity?.id,
              npo_id: opportunity?.npo_id,
              event_name: opportunity?.title,
              description: opportunity?.description,
              corporate_id: userId,
              status: isMicrosite ? "payment_done" : "payment_pending",
              event_date: formatDateWithTimezone(
                data.event_date,
                ensureTime24HourFormat(data.event_time),
                timezoneToUse || "UTC"
              ),
              event_time: data.event_time, // Keep the original event_time for backward compatibility
              host_id: host_id || null,
              expected_participants: data.volunteer_count,
              preferred_language: data.preferred_language,
              location: data.venue_address,
              meta: {
                npo_location: data.location,
                team_name: data.team_name || "",
                timezone: timezoneToUse || "UTC",
                event_cost: eventCost,
                currency: priceRecommendation?.currency || "USD",
              },
              paylater: eventCost == 0 || Boolean(isMicrosite),
            },
          });

          if (updateError) {
            throw updateError;
          }
          createdEvent = existingEventId;
        } else {
          // Create new event
          console.log("Creating new event");
          const { id: newEventId } = await createEvent({
            ...eventData,
            opportunity_catalog_id: opportunity?.id,
            npo_id: opportunity?.npo_id,
            event_name: opportunity?.title,
            description: opportunity?.description,
            corporate_id: userId,
            status: isMicrosite ? "payment_done" : "payment_pending",
            event_date: formatDateWithTimezone(
              data.event_date,
              ensureTime24HourFormat(data.event_time),
              timezoneToUse || "UTC"
            ),
            event_time: data.event_time, // Keep the original event_time for backward compatibility
            paylater: eventCost == 0 || Boolean(isMicrosite),
            expected_participants: data.volunteer_count,
            preferred_language: data.preferred_language,
            location: data.venue_address,
            meta: {
              npo_location: data.location,
              team_name: data.team_name || "",
              timezone: timezoneToUse || "UTC",
              event_cost: eventCost,
              currency: "USD",
            },
            ...(host_id ? { host_id } : {}),
          });

          if (!newEventId) {
            throw new Error("Failed to create event");
          }
          createdEvent = newEventId;
          sessionStorage.setItem("checkOutItemId", createdEvent);
          sessionStorage.setItem("retryUrl", window.location.href);
        }

        if (!createdEvent) {
          throw new Error("Failed to create/update event");
        }

        // Create Stripe checkout session
        if (!isMicrosite) {
          const response = await fetch(
            `${
              import.meta.env.VITE_CC_API_URL
            }/v1/hooks/stripe/checkout-session`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                eventCost,
                eventName: opportunity?.title || "Event Booking",
                eventId: createdEvent,
                email: data.email,
                phoneNumber: data.phone
                  ? `${selectedCountryCode}${data.phone}`
                  : undefined,
                successUrl: `${window.location.origin}/checkout/${npoSlug}/${catalogSlug}?payment=success&session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${window.location.origin}/checkout/${npoSlug}/${catalogSlug}?payment=cancelled&session_id={CHECKOUT_SESSION_ID}`,
              }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to create checkout session");
          }

          const { data: sessionData } = await response.json();
          // Redirect to Stripe checkout
          window.location.href = sessionData.sessionUrl;
        } else {
          // Redirect to success page
          window.location.href = `${window.location.origin}/checkout/${npoSlug}/${catalogSlug}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
        }
      } else {
        throw new Error("Failed to create temporary user");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setShowLoadingDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const source = searchParams.get("source");
  const ref = searchParams.get("ref");

  // Store session flag if coming from microsite
  React.useEffect(() => {
    if (source === "microsite" && ref) {
      sessionStorage.setItem("microsite_exp", "true");
      setIsMicrosite(true);
    }
  }, [source, ref]);

  if (isLoading || !opportunity) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">
              Getting the opportunity details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Opportunity Not Found
          </h2>
          <p className="text-gray-600">
            The opportunity you're looking for doesn't exist or has been
            removed.
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Quick Checkout"
        description="Quick and easy checkout process for volunteer events. No account required."
        noindex={true}
      />
      <LoadingDialog
        open={showLoadingDialog}
        onOpenChange={setShowLoadingDialog}
        title="Processing Payment"
        description="Please wait while we redirect you to the payment page..."
      />

      <div className="container max-w-6xl py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            {/* Contact details */}
            <div ref={contactRef}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                    1
                  </span>
                  Contact details
                </h2>
                {currentStep > 1 && editingSection !== 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingSection(1)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>

              {currentStep === 1 || editingSection === 1 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-gray-600 mb-6">
                      We'll use this information to send you confirmation and
                      updates about your booking
                    </p>

                    <div className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="first_name">First name{" "}<span className="text-red-500">*</span></Label>
                          <Input
                            id="first_name"
                            {...form.register("first_name")}
                            className={
                              form.formState.errors.first_name
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {form.formState.errors.first_name && (
                            <p className="text-sm text-red-500 mt-1">
                              {form.formState.errors.first_name.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="last_name">Last name{" "}<span className="text-red-500">*</span></Label>
                          <Input
                            id="last_name"
                            {...form.register("last_name")}
                            className={
                              form.formState.errors.last_name
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {form.formState.errors.last_name && (
                            <p className="text-sm text-red-500 mt-1">
                              {form.formState.errors.last_name.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email">Email{" "}<span className="text-red-500">*</span></Label>
                        <Input
                          id="email"
                          type="email"
                          {...form.register("email")}
                          className={
                            form.formState.errors.email ? "border-red-500" : ""
                          }
                        />
                        {form.formState.errors.email && (
                          <p className="text-sm text-red-500 mt-1">
                            {form.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone number (Optional)</Label>
                        <div className="flex gap-2">
                          <Select
                            value={selectedCountryCode}
                            onValueChange={setSelectedCountryCode}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue placeholder="Code" />
                            </SelectTrigger>
                            <SelectContent>
                              {ISD_CODES.map((code) => (
                                <SelectItem key={code.value} value={code.value}>
                                  {code.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            id="phone"
                            type="tel"
                            {...form.register("phone")}
                            className={
                              form.formState.errors.phone
                                ? "border-red-500"
                                : ""
                            }
                          />
                        </div>
                        {form.formState.errors.phone && (
                          <p className="text-sm text-red-500 mt-1">
                            {form.formState.errors.phone.message}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end">
                        {editingSection === 1 ? (
                          <div className="space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={async () => {
                                const isValid = await validateSection(1);
                                if (isValid) {
                                  setEditingSection(null);
                                  if (currentStep === 1) {
                                    setCurrentStep(2);
                                  }
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <Button onClick={handleNext}>Next</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-gray-600">Name</Label>
                        <p className="font-medium">
                          {form.getValues("first_name")}{" "}
                          {form.getValues("last_name")}
                        </p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Email</Label>
                        <p className="font-medium">{form.getValues("email")}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Phone</Label>
                        <p className="font-medium">{form.getValues("phone")}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">Location</Label>
                        <p className="font-medium">
                          {form.getValues("location")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Activity details */}
            <div ref={activityRef}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                    2
                  </span>
                  Activity details
                </h2>
                {currentStep > 2 && editingSection !== 2 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingSection(2)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>

              {currentStep === 2 || editingSection === 2 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      <EventDetailsForm
                        form={form}
                        format={opportunity?.format}
                        onTimezoneChange={(timezone) => {
                          form.setValue("timezone", timezone);
                          setSelectedTimezone(timezone);
                        }}
                      />

                      {/* Location dropdown using supported locations from price_recommendations */}
                      <Card className="p-6">
                        <h3 className="text-lg font-medium mb-4">Location</h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="location">Select Location{" "}<span className="text-red-500">*</span></Label>
                            <Select
                              value={form.watch("location")}
                              onValueChange={(value) =>
                                form.setValue("location", value)
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Choose a location" />
                              </SelectTrigger>
                              <SelectContent>
                                {opportunity?.price_recommendations
                                  ?.supported_locations ? (
                                  opportunity.price_recommendations.supported_locations.map(
                                    (location) => (
                                      <SelectItem
                                        key={location}
                                        value={location}
                                      >
                                        {location.replace(/-/g, ", ")}
                                      </SelectItem>
                                    )
                                  )
                                ) : (
                                  <SelectItem value="default">
                                    Default Location
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            {form.formState.errors.location && (
                              <p className="text-sm text-red-500 mt-1">
                                {form.formState.errors.location.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>

                      {/* Volunteer count field */}
                      <Card className="p-6">
                        <h3 className="text-lg font-medium mb-4">
                          Volunteer Count
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="volunteer_count">
                              Number of Volunteers{" "}<span className="text-red-500">*</span>
                            </Label>
                            <div className="flex items-center gap-4">
                              <Input
                                id="volunteer_count"
                                type="number"
                                min={opportunity?.min_volunteers || 1}
                                max={opportunity?.max_volunteers || 50}
                                {...form.register("volunteer_count", {
                                  valueAsNumber: true,
                                  required: "Volunteer count is required",
                                  min: {
                                    value: opportunity?.min_volunteers || 1,
                                    message: `Minimum ${
                                      opportunity?.min_volunteers || 1
                                    } volunteers required`,
                                  },
                                  max: {
                                    value: opportunity?.max_volunteers || 50,
                                    message: `Maximum ${
                                      opportunity?.max_volunteers || 50
                                    } volunteers allowed`,
                                  },
                                })}
                                className={
                                  form.formState.errors.volunteer_count
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              <div className="text-sm text-gray-500">
                                Min: {opportunity?.min_volunteers || 1}, Max:{" "}
                                {opportunity?.max_volunteers || 50}
                              </div>
                            </div>
                            {form.formState.errors.volunteer_count && (
                              <p className="text-sm text-red-500 mt-1">
                                {form.formState.errors.volunteer_count.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>

                      {/* Preferred Language field */}
                      <Card className="p-6">
                        <h3 className="text-lg font-medium mb-4">
                          Preferred Language
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="preferred_language">
                              Event Language{" "}<span className="text-red-500">*</span>
                            </Label>
                            <Select
                              value={form.watch("preferred_language")}
                              onValueChange={(value) =>
                                form.setValue("preferred_language", value)
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select language" />
                              </SelectTrigger>
                              <SelectContent>
                                {languageOptions.map((language) => (
                                  <SelectItem key={language} value={language}>
                                    {language}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {form.formState.errors.preferred_language && (
                              <p className="text-sm text-red-500 mt-1">
                                {
                                  form.formState.errors.preferred_language
                                    .message
                                }
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>

                      {/* Team Name field */}
                      <Card className="p-6">
                        <h3 className="text-lg font-medium mb-4">
                          Team Information
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="team_name">
                              Team Name (Optional)
                            </Label>
                            <Input
                              id="team_name"
                              placeholder="Enter your team name"
                              {...form.register("team_name")}
                            />
                          </div>

                          <div>
                            <Label htmlFor="venue_address">
                              Event Venue Address (Optional)
                            </Label>
                            <Textarea
                              id="venue_address"
                              placeholder="Enter the full address for the event"
                              {...form.register("venue_address", {
                                required: "Venue address is required",
                              })}
                              className={
                                form.formState.errors.venue_address
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {form.formState.errors.venue_address && (
                              <p className="text-sm text-red-500 mt-1">
                                {form.formState.errors.venue_address.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>

                      <div className="flex justify-end">
                        {editingSection === 2 ? (
                          <div className="space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={async () => {
                                const isValid = await validateSection(2);
                                if (isValid) {
                                  setEditingSection(null);
                                  if (currentStep === 2) {
                                    if (opportunity?.host_included) {
                                      setCurrentStep(4);
                                    } else {
                                      setCurrentStep(3);
                                    }
                                  }
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <Button onClick={handleNext}>Next</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                currentStep > 1 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label className="text-gray-600">
                            Date <span className="text-red-500">*</span>
                          </Label>
                          <p className="font-medium">
                            {form.getValues("event_date")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">
                            Time <span className="text-red-500">*</span>
                          </Label>
                          <p className="font-medium">
                            {form.getValues("event_time")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">
                            Location <span className="text-red-500">*</span>
                          </Label>
                          <p className="font-medium">
                            {form.getValues("location").replace(/-/g, ", ")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">
                            Volunteers <span className="text-red-500">*</span>
                          </Label>
                          <p className="font-medium">
                            {form.getValues("volunteer_count")} volunteers
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">
                            Language <span className="text-red-500">*</span>
                          </Label>
                          <p className="font-medium">
                            {form.getValues("preferred_language")}
                          </p>
                        </div>
                        {form.getValues("team_name") && (
                          <div>
                            <Label className="text-gray-600">Team</Label>
                            <p className="font-medium">
                              {form.getValues("team_name")}
                            </p>
                          </div>
                        )}

                        <div className="sm:col-span-2">
                          <Label className="text-gray-600">Venue Address</Label>
                          <p className="font-medium">
                            {form.getValues("venue_address")}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              )}
            </div>

            {/* Host Selection */}
            <div ref={hostRef}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                    3
                  </span>
                  Host Selection
                </h2>
                {!opportunity?.host_included &&
                  currentStep > 3 &&
                  editingSection !== 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingSection(3)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
              </div>

              {opportunity?.host_included ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <p>
                        Host is already included as part of the event package
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : currentStep === 3 || editingSection === 3 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-gray-600 mb-6">
                      Please select a host for your event
                    </p>

                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="host_id">Select Host</Label>
                        <Select
                          value={form.watch("host_id")}
                          onValueChange={(value) =>
                            form.setValue("host_id", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a host" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableHosts?.map((host) => (
                              <SelectItem key={host.id} value={host.id}>
                                <div className="flex justify-between items-center w-full">
                                  <span>{host.full_name}</span>
                                  <span className="text-gray-500 text-sm">
                                    {getHostPrice(host)}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {form.formState.errors.host_id && (
                          <p className="text-sm text-red-500 mt-1">
                            {form.formState.errors.host_id.message}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end">
                        {editingSection === 3 ? (
                          <div className="space-x-2">
                            <Button
                              variant="outline"
                              onClick={() => setEditingSection(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={async () => {
                                const isValid = await validateSection(3);
                                if (isValid) {
                                  setEditingSection(null);
                                  if (currentStep === 3) {
                                    setCurrentStep(4);
                                  }
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <Button onClick={handleNext}>Next</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                currentStep > 2 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <Label className="text-gray-600">Selected Host</Label>
                          <p className="font-medium">
                            {availableHosts?.find(
                              (h) => h.id === form.watch("host_id")
                            )?.full_name || "No host selected"}
                          </p>
                        </div>
                        <p className="text-gray-600">
                          {form.watch("host_id") &&
                            getHostPrice(
                              availableHosts?.find(
                                (h) => h.id === form.watch("host_id")
                              )
                            )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )
              )}
            </div>

            {/* Review and Book */}
            <div ref={reviewRef}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                    4
                  </span>
                  Review and Book
                </h2>
              </div>

              {(currentStep === 4 || editingSection === 4) && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label className="text-gray-600">Name</Label>
                          <p className="font-medium">
                            {form.getValues("first_name")}{" "}
                            {form.getValues("last_name")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">Email</Label>
                          <p className="font-medium">
                            {form.getValues("email")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">Phone</Label>
                          <p className="font-medium">
                            {form.getValues("phone")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">Location</Label>
                          <p className="font-medium">
                            {form.getValues("location")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">Date & Time</Label>
                          <p className="font-medium">
                            {form.getValues("event_date")} at{" "}
                            {form.getValues("event_time")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600">Event Cost</Label>
                          <div className="font-medium">
                            {(() => {
                              const priceRecommendation =
                                getPriceFromRecommendations();

                              if (priceRecommendation) {
                                if (
                                  priceRecommendation.pricingModel ===
                                  "per_volunteer"
                                ) {
                                  // For per_volunteer pricing, show the calculated cost
                                  const totalOriginalCost =
                                    priceRecommendation.cost *
                                    form.getValues("volunteer_count");
                                  const totalUsdCost =
                                    priceRecommendation.costUsd *
                                    form.getValues("volunteer_count");

                                  return (
                                    <>
                                      <div>
                                        {priceRecommendation.currency}{" "}
                                        {totalOriginalCost.toFixed(2)}
                                        <span className="text-sm text-gray-500 ml-1">
                                          ({form.getValues("volunteer_count")}{" "}
                                          volunteers)
                                        </span>
                                      </div>
                                      {priceRecommendation.currency !==
                                        "USD" && (
                                        <div className="text-sm text-gray-500">
                                          USD {totalUsdCost.toFixed(2)}{" "}
                                          equivalent
                                        </div>
                                      )}
                                    </>
                                  );
                                } else {
                                  return (
                                    <>
                                      <div>
                                        {priceRecommendation.currency}{" "}
                                        {priceRecommendation.cost}
                                      </div>
                                      {priceRecommendation.currency !==
                                        "USD" && (
                                        <div className="text-sm text-gray-500">
                                          USD {priceRecommendation.costUsd}{" "}
                                          equivalent
                                        </div>
                                      )}
                                    </>
                                  );
                                }
                              } else {
                                return `USD ${opportunity?.event_cost || 0}`;
                              }
                            })()}
                          </div>
                        </div>
                      </div>

                      <Button
                        className="w-full"
                        onClick={form.handleSubmit(onSubmit)}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Processing..."
                          : isMicrosite
                          ? "Book now, Pay later"
                          : "Complete Booking"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:sticky lg:top-8 lg:self-start">
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <img
                    src={
                      opportunity.opportunity?.image_url ||
                      opportunity.images?.[0]?.image_url ||
                      "https://via.placeholder.com/100"
                    }
                    alt={opportunity.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <h3 className="font-medium">{opportunity.title}</h3>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users2 className="h-4 w-4" />
                    <span>
                      {opportunity.min_volunteers}-{opportunity.max_volunteers}{" "}
                      volunteers
                    </span>
                  </div>
                  {form.watch("volunteer_count") && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <UserPlus className="h-4 w-4" />
                      <span>
                        {form.watch("volunteer_count")} volunteers selected
                      </span>
                    </div>
                  )}
                  {form.watch("preferred_language") && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Globe className="h-4 w-4" />
                      <span>{form.watch("preferred_language")}</span>
                    </div>
                  )}
                  {form.watch("team_name") && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="h-4 w-4" />
                      <span>Team: {form.watch("team_name")}</span>
                    </div>
                  )}

                  {form.watch("venue_address") && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="h-4 w-4" />
                      <span title={form.watch("venue_address")}>
                        {form.watch("venue_address").length > 30
                          ? `${form.watch("venue_address").substring(0, 30)}...`
                          : form.watch("venue_address")}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {opportunity?.format_details ? (
                      isValidUrl(opportunity.format_details) ? (
                        <a
                          href={opportunity.format_details}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Location details
                        </a>
                      ) : (
                        <span title={opportunity.format_details}>
                          {opportunity.format_details.length > 30
                            ? `${opportunity.format_details.substring(
                                0,
                                30
                              )}...`
                            : opportunity.format_details}
                        </span>
                      )
                    ) : (
                      <span>Location not specified</span>
                    )}
                  </div>
                  {form.watch("event_date") && form.watch("event_time") && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {form.getValues("event_date")} at{" "}
                        {form.getValues("event_time")} (
                        {form.getValues("timezone") || selectedTimezone})
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex flex-col gap-3">
                    {opportunity?.price_recommendations ? (
                      // Display price from recommendations if available
                      <>
                        {(() => {
                          const priceRecommendation =
                            getPriceFromRecommendations();
                          if (!priceRecommendation) return null;

                          return (
                            <>
                              <div className="flex justify-between items-center text-sm">
                                <span>
                                  Base cost (
                                  {priceRecommendation.pricingModel ===
                                  "per_event"
                                    ? "per event"
                                    : priceRecommendation.pricingModel ===
                                      "per_volunteer"
                                    ? "per volunteer"
                                    : "total"}
                                  )
                                </span>
                                <span>
                                  {priceRecommendation.currency}{" "}
                                  {priceRecommendation.cost}
                                </span>
                              </div>

                              {!priceRecommendation.includedFees &&
                                Object.entries(
                                  priceRecommendation.additionalFees
                                ).map(([key, fee]: [string, any]) => {
                                  if (fee.amount > 0) {
                                    const feeLabel = key.replace(/_/g, " ");
                                    const feeType =
                                      fee.fee_type === "per_volunteer"
                                        ? "per volunteer"
                                        : fee.fee_type === "per_event"
                                        ? "per event"
                                        : "total";

                                    return (
                                      <div
                                        key={key}
                                        className="flex justify-between items-center text-sm"
                                      >
                                        <span>
                                          {feeLabel.charAt(0).toUpperCase() +
                                            feeLabel.slice(1)}{" "}
                                          ({feeType})
                                        </span>
                                        <span>
                                          {priceRecommendation.currency}{" "}
                                          {fee.amount}
                                        </span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })}

                              {priceRecommendation.additionalDetails && (
                                <div className="text-sm text-gray-600 mt-2">
                                  <span>
                                    Note:{" "}
                                    {priceRecommendation.additionalDetails}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between items-center font-medium pt-3 border-t">
                                <span>Total price</span>
                                <div className="text-right">
                                  {priceRecommendation.pricingModel ===
                                    "per_volunteer" &&
                                  form.watch("volunteer_count") > 0 ? (
                                    <>
                                      <div>
                                        {priceRecommendation.currency}{" "}
                                        {(
                                          priceRecommendation.cost *
                                          form.watch("volunteer_count")
                                        ).toFixed(2)}
                                        <span className="text-xs text-gray-500 ml-1">
                                          ({form.watch("volunteer_count")}{" "}
                                          volunteers)
                                        </span>
                                      </div>
                                      {priceRecommendation.currency !==
                                        "USD" && (
                                        <div className="text-sm text-gray-500">
                                          USD{" "}
                                          {(
                                            priceRecommendation.costUsd *
                                            form.watch("volunteer_count")
                                          ).toFixed(2)}{" "}
                                          equivalent
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <div>
                                        {priceRecommendation.currency}{" "}
                                        {priceRecommendation.cost}
                                      </div>
                                      {priceRecommendation.currency !==
                                        "USD" && (
                                        <div className="text-sm text-gray-500">
                                          USD {priceRecommendation.costUsd}{" "}
                                          equivalent
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      // Default pricing display if no recommendations
                      <>
                        <div className="flex justify-between items-center text-sm">
                          <span>Event cost</span>
                          <span>USD {opportunity.event_cost || 0}</span>
                        </div>
                        {form.watch("host_id") &&
                          !opportunity?.host_included && (
                            <div className="flex justify-between items-center text-sm">
                              <span>Host cost</span>
                              <span>
                                {getHostPrice(
                                  availableHosts?.find(
                                    (h) => h.id === form.watch("host_id")
                                  )
                                )}
                              </span>
                            </div>
                          )}
                        <div className="flex justify-between items-center font-medium pt-3 border-t">
                          <span>Total price</span>
                          <span>
                            USD{" "}
                            {(opportunity.event_cost || 0) +
                              (!opportunity?.host_included &&
                              form.watch("host_id")
                                ? Number(
                                    availableHosts
                                      ?.find(
                                        (h) => h.id === form.watch("host_id")
                                      )
                                      ?.host_rate_card?.find(
                                        (card) =>
                                          card.format ===
                                          opportunity?.opportunity?.format
                                      )?.rate || 0
                                  )
                                : 0)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between items-center font-medium">
                    <span>Total price</span>
                    <span>USD {opportunity.event_cost || 0}</span>
                  </div>
                </div> */}
              </CardContent>
            </Card>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Book with confidence</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    {
                      CANCELLATION_POLICIES.find(
                        (p) => p.value === opportunity.cancellation_policy
                      )?.label
                    }{" "}
                    available
                  </li>
                  <li>Lowest price guarantee</li>
                  <li>
                    <a
                      href="/contact"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      24/7 customer support
                    </a>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}
